<template>
  <div class="dispensary-report">
    <ReportFormat @show_result="show_result" :is_table_show="false" :isLoading="loading">
      <template #report-form>
        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Reporting Year</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  autocomplete
                  class="w-full"
                  :value="year"
                  @input="(item) => (year = item && item.value)"
                  name="Reporting Year"
                  placeholder="Select Year"
                  :options="yearOptions"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('Reporting Year')">{{ errors.first('Reporting Year') }}</span>
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Reporting Month</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  autocomplete
                  class="w-full"
                  :value="month"
                  @input="(item) => (month = item && item.value)"
                  name="Reporting Month"
                  placeholder="Please Select Month"
                  :options="monthOptions"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('Reporting Month')">{{ errors.first('Reporting Month') }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </ReportFormat>

    <DispensaryReportData ref="report" @download_excel_report="exportReport" v-if="Object.keys(reports_data).length" :year="year" :month="month" />
  </div>
</template>

<script>
import moment from 'moment'
import XLSX from 'xlsx'
import { mapMutations, mapState } from 'vuex'
import { weightMixins } from '@/utils/weightType'
import { paymentMixins } from '@/utils/paymentTypes'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import DispensaryReportData from '@/views/reports/components/DispensaryReportData.vue'
import Select2 from '@/components/custom/form-elements/Select2.vue'

export default {
  name: 'ProcessorReport',

  components: {
    ReportFormat,
    DispensaryReportData,
    Select2
  },

  data() {
    return {
      loading: false,
      page: 1,
      limit: 0,
      search: '',
      yearOptions: this.getYearsOptions(),
      year: moment().year(),
      monthOptions: this.getMonthOptions(),
      month: moment().month() + 1
    }
  },

  mixins: [weightMixins, paymentMixins],

  methods: {
    moment,
    ...mapMutations('report', { set_state: 'SET_STATE' }),

    getYearsOptions() {
      const years = []
      let startYear = moment('2019', 'YYYY').format('YYYY')
      const lastYear = moment().format('YYYY')

      while (startYear <= lastYear) {
        years.push({ label: startYear, value: startYear })
        startYear++
      }

      return years
    },

    getMonthOptions() {
      return moment.months().reduce((acc, item, index) => {
        const tempObj = {}
        tempObj.value = index + 1
        tempObj.label = item

        acc.push(tempObj)

        return acc
      }, [])
    },

    // fetch dispansary report
    async show_result() {
      if (!(await this.$validator.validate())) {
        return false
      }
      this.loading = true

      const payload = {
        year: this.year,
        month: this.month
      }

      this.$store
        .dispatch('report/getDispensaryReport', payload)
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    // generate excel
    exportReport() {
      this.generating = true

      const wb = XLSX.utils.book_new()

      // The amount of marijuana on hand at the beginning of the reporting period.
      const ws = XLSX.utils.aoa_to_sheet([['The amount of marijuana on hand at the beginning of the reporting period.']], { skipHeader: true, origin: 'A1' })
      let amountOfMarijuanaAtBeginning = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountOfMarijuanaAtBeginning.$el)
      amountOfMarijuanaAtBeginning = XLSX.utils.sheet_to_json(amountOfMarijuanaAtBeginning)
      XLSX.utils.sheet_add_json(ws, amountOfMarijuanaAtBeginning, { origin: 'A2' })

      // The amount of marijuana purchased from a licensed grower.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana purchased from a licensed grower.']], { origin: 'A18' })
      let marijuanaPurchasedFromGrower = XLSX.utils.table_to_sheet(this.$refs.report.$refs.marijuanaPurchasedFromGrower.$el)
      marijuanaPurchasedFromGrower = XLSX.utils.sheet_to_json(marijuanaPurchasedFromGrower)
      XLSX.utils.sheet_add_json(ws, marijuanaPurchasedFromGrower, { origin: 'A19' })

      // The amount of marijuana purchased from a licensed processor.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana purchased from a licensed processor.']], { origin: 'A25' })
      let marijuanaPurchasedFromProcessor = XLSX.utils.table_to_sheet(this.$refs.report.$refs.marijuanaPurchasedFromProcessor.$el)
      marijuanaPurchasedFromProcessor = XLSX.utils.sheet_to_json(marijuanaPurchasedFromProcessor)
      XLSX.utils.sheet_add_json(ws, marijuanaPurchasedFromProcessor, { origin: 'A26' })

      // The amount of marijuana sold to a licensed patient or caregiver.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana sold to a licensed patient or caregiver.']], { origin: 'A42' })
      let marijuanaSoldToPatient = XLSX.utils.table_to_sheet(this.$refs.report.$refs.marijuanaSoldToPatient.$el)
      marijuanaSoldToPatient = XLSX.utils.sheet_to_json(marijuanaSoldToPatient)
      XLSX.utils.sheet_add_json(ws, marijuanaSoldToPatient, { origin: 'A43' })

      // Total dollar amount of all sales of medical marijuana to patients and caregivers.
      XLSX.utils.sheet_add_aoa(ws, [['Total dollar amount of all sales of medical marijuana to patients and caregivers.']], { origin: 'A59' })
      XLSX.utils.sheet_add_aoa(ws, [[`$${paymentMixins.methods.mathRound(this.reports_data.total_dollar_amount_of_sales_to_patient, 2)}` || 0]], { origin: 'A60', skipHeader: true })

      // Total dollar amount of all gross receipts tax (7%) collected from sales of medical marijuana to patients and caregivers.
      XLSX.utils.sheet_add_aoa(ws, [['Total dollar amount of all gross receipts tax (7%) collected from sales of medical marijuana to patients and caregivers.']], { origin: 'A62' })
      XLSX.utils.sheet_add_aoa(ws, [[`$${paymentMixins.methods.mathRound(this.reports_data.amount_of_all_excise_tax_to_patients_and_caregiver, 2)}` || 0]], { origin: 'A63', skipHeader: true })

      // The amount of marijuana transported to your business during this reporting month.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana transported to your business during this reporting month.']], { origin: 'A65' })
      let marijuanaTransported = XLSX.utils.table_to_sheet(this.$refs.report.$refs.marijuanaTransported.$el)
      marijuanaTransported = XLSX.utils.sheet_to_json(marijuanaTransported)
      XLSX.utils.sheet_add_json(ws, marijuanaTransported, { origin: 'A66' })

      // Please list all transporter ID's that have transported for your business this reporting month.
      XLSX.utils.sheet_add_aoa(ws, [['Please list all transporter ID`s that have transported for your business this reporting month.']], { origin: 'A82' })
      let transportersId = XLSX.utils.table_to_sheet(this.$refs.report.$refs.transportersId.$el)
      transportersId = XLSX.utils.sheet_to_json(transportersId)
      XLSX.utils.sheet_add_json(ws, transportersId, { origin: 'A83', skipHeader: true })

      // The amount of marijuana on hand at the end of the reporting period.
      XLSX.utils.sheet_add_aoa(ws, [['The amount of marijuana on hand at the end of the reporting period.']], {
        origin: `A${83 + this.reports_data.list_all_transporter_id_from_sales.transporters.length + 1}`
      })
      let amountOfMarijuanaAtEnd = XLSX.utils.table_to_sheet(this.$refs.report.$refs.amountOfMarijuanaAtEnd.$el)
      amountOfMarijuanaAtEnd = XLSX.utils.sheet_to_json(amountOfMarijuanaAtEnd)
      XLSX.utils.sheet_add_json(ws, amountOfMarijuanaAtEnd, { origin: `A${83 + this.reports_data.list_all_transporter_id_from_sales.transporters.length + 2}` })

      const mergedCells = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
        { s: { r: 17, c: 0 }, e: { r: 17, c: 2 } },
        { s: { r: 24, c: 0 }, e: { r: 24, c: 2 } },
        { s: { r: 41, c: 0 }, e: { r: 41, c: 10 } },
        { s: { r: 64, c: 0 }, e: { r: 64, c: 2 } },
        {
          s: { r: 83 + this.reports_data.list_all_transporter_id_from_sales.transporters.length + 1, c: 0 },
          e: { r: 83 + this.reports_data.list_all_transporter_id_from_sales.transporters.length + 1, c: 2 }
        }
      ]

      ws['!merges'] = mergedCells

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'DispensaryReport.xlsx')

      this.generating = false
    }
  },

  computed: {
    ...mapState('report', ['records_loading', 'reports_data'])
  },

  filters: {
    //weightFilter rounded conversation
    weightFilter(value) {
      return weightMixins.methods.mathRound(value || 0, 3)
    },

    // currencyFilter rounded to 2 point
    currencyFilter(value) {
      return paymentMixins.methods.mathRound(Number(value), 2)
    }
  },

  async beforeDestroy() {
    await this.set_state({ action: 'reports_data', data: [] })
  }
}
</script>
